import { Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';
import { NgForOf, NgIf } from '@angular/common';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { FirstCasePipe } from '@shared/pipes/first-case.pipe';
import { Link } from '@shared/interfaces/link';
import { Content } from '@shared/interfaces/content';
import { SearchModalService } from '@shared/services/search-modal.service';
import { ENVIRONMENT_CONFIG } from '@shared/injection-token/environment-config';

@Component({
  selector: 'app-modal-search',
  imports: [ClickOutsideDirective, NgIf, NgForOf, FirstCasePipe],
  templateUrl: './modal-search.component.html',
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({
          opacity: 0,
        }),
        animate(
          '200ms',
          style({
            opacity: 1,
          })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms',
          style({
            opacity: 0,
          })
        ),
      ]),
    ]),
    trigger('open', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(0.25rem)',
        }),
        animate(
          '200ms',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          })
        ),
      ]),
      transition(':leave', [
        style({
          opacity: 1,
          transform: 'translateY(0)',
        }),
        animate(
          '300ms',
          style({
            opacity: 0,
            transform: 'translateY(0.25rem)',
          })
        ),
      ]),
    ]),
  ],
})
export class ModalSearchComponent {
  @ViewChild('searchInputRef') searchInputRef!: ElementRef<HTMLInputElement>;
  @Input() commonSearches?: Link[];
  @Input() contents!: { [key in string]: Content }[];

  isOpen = false;
  isSearching = false;
  results: Link[] = [];
  environmentConfig = inject(ENVIRONMENT_CONFIG);
  readonly routesPath = this.environmentConfig.routes;
  constructor(
    private searchModalService: SearchModalService,
    private router: Router
  ) {
    this.searchModalService.getIsOpenModal$().subscribe((isOpenModal) => {
      this.isOpen = isOpenModal;
      if (this.isOpen) {
        setTimeout(() => this.searchInputRef.nativeElement.focus(), 1500);
      } else {
        this.resetSearch();
      }
    });
  }

  onSearch(event: KeyboardEvent) {
    const search = this.normalize((event.target as HTMLInputElement).value);
    if (search.length > 2) {
      this.isSearching = true;
      this.results = [];
    } else {
      this.isSearching = false;
    }
  }

  onClickResult(url: string) {
    this.resetSearch();
    void this.router.navigateByUrl(url);
  }

  resetSearch(isOpen = false) {
    this.results = [];
    this.isSearching = false;
    this.searchInputRef.nativeElement.value = '';
    this.isOpen = isOpen;
  }

  private normalize(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
